import React, { useEffect, useState } from 'react';
import { useTeamDashboard } from '../TeamDashboardContext';
import { apiGet, apiPost, apiPatch } from '../api';
import './invoices.css';

const INVOICES = [
  { id: 'INV-018', initials: 'AK', name: 'Arjun Kumar', role: 'owner', rate: '₹500/hr', period: 'Apr 14-20', hrs: '12.5 hrs', amount: '₹6,250', status: 'paid', hoursNum: 12.5, rateNum: 500 },
  { id: 'INV-019', initials: 'SR', name: 'Sneha Rao', role: 'lead', rate: '₹400/hr', period: 'Apr 14-20', hrs: '18 hrs', amount: '₹7,200', status: 'paid', hoursNum: 18, rateNum: 400 },
  { id: 'INV-020', initials: 'RM', name: 'Rahul Mehta', role: 'dev', rate: '₹300/hr', period: 'Apr 14-20', hrs: '8 hrs', amount: '₹2,400', status: 'pending', hoursNum: 8, rateNum: 300 },
  { id: 'INV-021', initials: 'PK', name: 'Priya Kapoor', role: 'dev', rate: '₹500/hr', period: 'Apr 14-20', hrs: '4 hrs', amount: '₹2,000', status: 'pending', hoursNum: 4, rateNum: 500 },
  { id: 'INV-017', initials: 'AK', name: 'Arjun Kumar', role: 'owner', rate: '₹500/hr', period: 'Apr 7-13', hrs: '11 hrs', amount: '₹5,500', status: 'paid', hoursNum: 11, rateNum: 500 },
];

export default function InvoicesPage() {
  const { showToast, isReal, teamSlug } = useTeamDashboard();
  const [mounted, setMounted] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  const fetchInvoices = async () => {
    if (!isReal) {
      setInvoices(INVOICES);
      return;
    }
    try {
      const json = await apiGet<any>(`/${teamSlug}/invoices`);
      const list = json && typeof json === 'object' && 'data' in json ? json.data : json;
      setInvoices(Array.isArray(list) ? list : []);
    } catch {
      // Fallback silently
    }
  };

  useEffect(() => {
    void fetchInvoices();
  }, [isReal, teamSlug]);

  const generateInvoices = async () => {
    if (!isReal) {
      showToast('Invoices generated', '4 invoices created for Apr 21-27', 'success');
      return;
    }
    setLoading(true);
    try {
      const json = await apiPost<any>(`/${teamSlug}/invoices/generate`, {});
      const generated = json && typeof json === 'object' && 'data' in json ? json.data : json;
      if (Array.isArray(generated) && generated.length > 0) {
        showToast('Invoices', `${generated.length} invoice(s) generated successfully!`, 'success');
        void fetchInvoices();
      } else {
        showToast('Invoices', 'No outstanding hours to generate invoices for.', 'info');
      }
    } catch {
      showToast('Invoices', 'Failed to generate weekly invoices.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const payInvoice = async (invoiceId: string, amountStr: string) => {
    if (!isReal) {
      showToast('Payment', `Paying ${amountStr}...`, 'success');
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'paid' } : inv));
      return;
    }
    try {
      const json = await apiPatch<any>(`/${teamSlug}/invoices/${invoiceId}`, { status: 'paid' });
      const updated = json && typeof json === 'object' && 'data' in json ? json.data : json;
      if (updated) {
        setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'paid' } : inv));
        showToast('Payment successful', `Paid invoice #${invoiceId}`, 'success');
      }
    } catch {
      showToast('Payment error', 'Failed to process invoice payment.', 'error');
    }
  };

  const totalInvoiced = invoices.reduce((acc, curr) => acc + (curr.amount ? (typeof curr.amount === 'number' ? curr.amount : parseFloat(String(curr.amount).replace(/[^\d]/g, ''))) : 0), 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((acc, curr) => acc + (curr.amount ? (typeof curr.amount === 'number' ? curr.amount : parseFloat(String(curr.amount).replace(/[^\d]/g, ''))) : 0), 0);
  const totalPending = invoices.filter(inv => inv.status !== 'paid').reduce((acc, curr) => acc + (curr.amount ? (typeof curr.amount === 'number' ? curr.amount : parseFloat(String(curr.amount).replace(/[^\d]/g, ''))) : 0), 0);
  const pendingCount = invoices.filter(inv => inv.status !== 'paid').length;

  const formatCurrency = (val: number) => {
    return '₹' + val.toLocaleString('en-IN');
  };

  const handleDownloadPDF = (invId: string) => {
    showToast('Download', `Downloading invoice ${invId}.pdf...`, 'info');
    const element = document.createElement('a');
    const file = new Blob([`Invoice ${invId}\nStatus: Paid\nAmount: ${selectedInvoice?.amount || ''}\nGenerated via Scrudix Team Studio.`], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${invId}_receipt.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const { theme } = useTeamDashboard();
  return (
    <div className="td-invoices" data-theme={theme}>
      <div className="ptitle">Invoices</div>
      <div className="psub">Review generated developer invoices and verify status of payouts.</div>

      {/* KPI Cards */}
      <div className={`g3 mb fu${mounted ? ' in' : ''}`}>
        <div className="sc tilt">
          <div className="sc-eye">Total Invoiced</div>
          <div className="sc-val">{formatCurrency(totalInvoiced)}</div>
          <div className="sc-change" style={{ color: 'var(--text3)' }}>Current fiscal quarter total</div>
        </div>
        <div className="sc tilt">
          <div className="sc-eye">Total Paid</div>
          <div className="sc-val" style={{ color: 'var(--green)' }}>{formatCurrency(totalPaid)}</div>
          <div className="sc-change up">Paid out to developers</div>
        </div>
        <div className="sc tilt">
          <div className="sc-eye">Outstanding</div>
          <div className="sc-val" style={{ color: 'var(--amber)' }}>{formatCurrency(totalPending)}</div>
          <div className="sc-change" style={{ color: 'var(--amber)' }}>{pendingCount} pending payout</div>
        </div>
      </div>

      {/* Invoices List */}
      <div className={`card fu${mounted ? ' in' : ''}`} style={{ transitionDelay: '.05s' }}>
        <div className="ch">
          <span className="ct">All Invoices</span>
          <button disabled={loading} onClick={generateInvoices} className="btn-p" style={{ fontSize: 11, padding: '5px 12px' }}>
            {loading ? 'Generating...' : 'Generate Invoices'}
          </button>
        </div>

        {invoices.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
            No invoices generated yet. Click above to create new invoices.
          </div>
        ) : (
          <div>
            {invoices.map(inv => {
              const displayId = inv.number || inv.id;
              const displayPeriod = inv.period || `${inv.periodStart}-${inv.periodEnd}`;
              const displayHrs = inv.hrs || `${inv.hours} hrs`;
              const displayAmount = typeof inv.amount === 'number' ? formatCurrency(inv.amount) : String(inv.amount);
              return (
                <div key={inv.id} className="mr" onClick={() => setSelectedInvoice(inv)}>
                  <div className="msh" />
                  <div className="avr" style={{ background: 'var(--blue)22', color: 'var(--blue)' }}>
                    {inv.memberInitials || inv.initials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="mn">{inv.memberName || inv.name}</div>
                    <div className="mm">{inv.memberRole || inv.role} · #{displayId}</div>
                  </div>

                  <div className="mrr" style={{ display: 'flex', alignItems: 'center', gap: 12 }} onClick={e => e.stopPropagation()}>
                    <div style={{ textAlign: 'right', marginRight: 4 }}>
                      <div className="mn" style={{ fontSize: 13, fontWeight: 600 }}>{displayAmount}</div>
                      <div className="mm" style={{ fontSize: 10 }}>{displayPeriod} ({displayHrs})</div>
                    </div>
                    <span className={`pill ${inv.status === 'paid' ? 'pg' : 'pa'}`} style={{ textTransform: 'capitalize' }} onClick={() => setSelectedInvoice(inv)}>
                      {inv.status}
                    </span>
                    <div>
                      {inv.status === 'paid' ? (
                        <button onClick={() => handleDownloadPDF(displayId)} className="clink" style={{ padding: '4px 8px' }}>
                          ↓ PDF
                        </button>
                      ) : (
                        <button onClick={() => payInvoice(inv.id, displayAmount)} className="btn-p" style={{ fontSize: 10, padding: '4px 10px', background: 'var(--green)', border: 'none' }}>
                          Pay
                        </button>
                      )}
                    </div>
                  </div>
                  <span className="chv" onClick={() => setSelectedInvoice(inv)}>›</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="overlay open" onClick={() => setSelectedInvoice(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="mhdr">
              <div>
                <div className="mttl">Invoice Details</div>
                <div className="msub">#{selectedInvoice.number || selectedInvoice.id} · {selectedInvoice.period || `${selectedInvoice.periodStart}-${selectedInvoice.periodEnd}`}</div>
              </div>
              <button className="mclose" onClick={() => setSelectedInvoice(null)}>✕</button>
            </div>

            <div className="mbody" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Bill Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
                <div>
                  <span style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', color: 'var(--text3)', fontWeight: 600, marginBottom: 4 }}>Developer</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{selectedInvoice.memberName || selectedInvoice.name}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text4)' }}>{selectedInvoice.memberRole || selectedInvoice.role}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', color: 'var(--text3)', fontWeight: 600, marginBottom: 4 }}>Status</span>
                  <span className={`pill ${selectedInvoice.status === 'paid' ? 'pg' : 'pa'}`} style={{ textTransform: 'capitalize', display: 'inline-block' }}>
                    {selectedInvoice.status}
                  </span>
                </div>
              </div>

              {/* Line items table */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h4 style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text3)', letterSpacing: 0.5 }}>EARNINGS AUDIT</h4>
                <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border2)', fontFamily: 'var(--mono)', fontSize: 11, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Logged Hours:</span>
                    <span style={{ color: 'var(--text)' }}>{selectedInvoice.hrs || `${selectedInvoice.hours} hrs`}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Hourly Rate:</span>
                    <span style={{ color: 'var(--text)' }}>₹{selectedInvoice.rateNum || 400}/hr</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 6, fontWeight: 600, color: 'var(--text)' }}>
                    <span>Gross Earnings:</span>
                    <span>{typeof selectedInvoice.amount === 'number' ? formatCurrency(selectedInvoice.amount) : String(selectedInvoice.amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--red)' }}>
                    <span>TDS Deduction (10%):</span>
                    <span>-₹{Math.round((selectedInvoice.rateNum || 400) * (selectedInvoice.hoursNum || 8) * 0.1).toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 6, fontWeight: 800, color: 'var(--accent)', fontSize: 12 }}>
                    <span>Net Payout:</span>
                    <span>₹{Math.round((selectedInvoice.rateNum || 400) * (selectedInvoice.hoursNum || 8) * 0.9).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mfoot">
              <button onClick={() => setSelectedInvoice(null)} className="btn-s">
                Close
              </button>
              {selectedInvoice.status === 'paid' ? (
                <button onClick={() => handleDownloadPDF(selectedInvoice.number || selectedInvoice.id)} className="btn-p">
                  Download PDF
                </button>
              ) : (
                <button onClick={() => {
                  payInvoice(selectedInvoice.id, selectedInvoice.amount);
                  setSelectedInvoice(null);
                }} className="btn-p" style={{ background: 'var(--green)', border: 'none' }}>
                  Pay Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
